import React, { useEffect, useState } from 'react';
import { abrirArchivo, documentosDe, facturasDe, mensajeDeError } from '../api';

const fmtFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const icono = (tipo) => {
  switch ((tipo || '').toLowerCase()) {
    case 'pdf': return '📄';
    case 'xlsx': case 'xls': case 'csv': return '📊';
    case 'doc': case 'docx': return '📝';
    case 'jpg': case 'jpeg': case 'png': case 'tif': case 'tiff': return '🖼️';
    case 'zip': return '🗜️';
    default: return '📎';
  }
};

/**
 * Los archivos de un trámite, en una ventana.
 *
 * `clase` decide qué se pide: los documentos de operaciones o la factura. Son dos endpoints
 * distintos a propósito —no una lista con un filtro— para que la factura nunca se cuele en la
 * otra lista por un descuido de filtrado.
 *
 * Cada archivo se abre pidiendo una URL temporal, que el backend solo entrega después de
 * comprobar que ese archivo es del cliente que lo pide.
 */
export default function ModalArchivos({ tramite, clase, alCerrar }) {
  const [archivos, setArchivos] = useState(null);
  const [error, setError] = useState('');
  const [abriendo, setAbriendo] = useState(null);

  const esFactura = clase === 'factura';

  useEffect(() => {
    let vivo = true;
    const pedir = esFactura ? facturasDe : documentosDe;
    pedir(tramite.id)
      .then(({ data }) => { if (vivo) setArchivos(data); })
      .catch((err) => { if (vivo) setError(mensajeDeError(err, 'No se pudieron cargar los archivos.')); });
    return () => { vivo = false; };
  }, [tramite.id, esFactura]);

  // Cerrar con Escape: la ventana tapa la tabla y hay que poder salir sin buscar el botón.
  useEffect(() => {
    const alTeclear = (e) => { if (e.key === 'Escape') alCerrar(); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [alCerrar]);

  const abrir = async (archivo) => {
    setAbriendo(archivo.id);
    setError('');
    try {
      await abrirArchivo(archivo.id, false);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo abrir el archivo.'));
    } finally {
      setAbriendo(null);
    }
  };

  return (
    <div
      onClick={alCerrar}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, padding: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.55)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tarjeta"
        style={{
          width: '100%', maxWidth: 520, maxHeight: '84vh',
          display: 'flex', flexDirection: 'column', boxShadow: 'var(--sombra-md)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gris-borde)' }}>
          <div style={{ fontSize: 15.5, fontWeight: 800 }}>
            {esFactura ? 'Factura' : 'Documentos'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--gris-texto)', marginTop: 2 }}>
            {tramite.documentoAsociado}
          </div>
        </div>

        <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
          {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}

          {archivos === null && !error && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--gris-texto)' }}>
              Cargando…
            </div>
          )}

          {archivos !== null && archivos.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--gris-texto)', fontSize: 13.5 }}>
              {esFactura
                ? 'Todavía no hay factura para este trámite.'
                : 'Todavía no hay documentos para este trámite.'}
            </div>
          )}

          {(archivos || []).map((archivo) => (
            <button
              key={archivo.id}
              type="button"
              onClick={() => abrir(archivo)}
              disabled={abriendo === archivo.id}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 11, textAlign: 'left',
                padding: '11px 12px', marginBottom: 8, borderRadius: 10, cursor: 'pointer',
                background: 'var(--gris-claro)', border: '1px solid var(--gris-borde)',
                color: 'var(--negro-texto)',
              }}
            >
              <span style={{ fontSize: 19 }}>{icono(archivo.tipo)}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{
                  display: 'block', fontSize: 13.5, fontWeight: 700,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {archivo.nombre}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--gris-texto)' }}>
                  {fmtFecha(archivo.fecha)}
                </span>
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--azul-medio)' }}>
                {abriendo === archivo.id ? 'Abriendo…' : 'Abrir'}
              </span>
            </button>
          ))}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gris-borde)', textAlign: 'right' }}>
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
